import { ChevronRight } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { StatusBadge } from "@/components/status-badge"
import { Badge } from "@/components/ui/badge"
import { Card } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import type { LotListItemWithQC } from "@/features/lots/api/types"
import { lotNetMT, lotUsinageNumber } from "@/features/lots/lib/lot-math"
import { lotStatusTone } from "@/features/lots/lib/lot-status"
import { LOT_TARGET_BAGS } from "@/features/milling/lib/quality"
import { certificateTranslationKey } from "@/lib/certificates"
import { cn } from "@/lib/utils"
import { paths } from "@/routes/paths"

const headCellClass =
  "h-11 px-4 text-muted-foreground text-xs font-medium uppercase tracking-wide"
const bodyCellClass = "px-4 py-4"

interface LotsTableProps {
  lots: LotListItemWithQC[]
}

// Pull the contract context off the first source. Every source on a
// single lot resolves to the same contract (one client, one cert set),
// so any source row will do.
function lotContext(lot: LotListItemWithQC): {
  client: string | null
  certifications: string[]
} {
  const order = lot.sources?.[0]?.load?.order
  return {
    client: order?.exporter?.clientName ?? null,
    certifications: order?.certificates ?? [],
  }
}

// A lot is bound to its first OT for life — surface that link
// regardless of archive/terminer state so the column shows the OT
// number even after shipment.
function boundOTLink(lot: LotListItemWithQC) {
  return (lot.otLinks ?? []).find((l) => l.ot) ?? null
}

export function LotsTable({ lots }: LotsTableProps) {
  const { t } = useTranslation("lots")
  const navigate = useNavigate()

  return (
    <Card className="overflow-hidden p-0">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40 hover:bg-muted/40">
              <TableHead className={headCellClass}>
                {t("list.table.lot")}
              </TableHead>
              <TableHead className={`${headCellClass} text-right`}>
                {t("list.table.bags")}
              </TableHead>
              <TableHead className={`${headCellClass} text-right`}>
                {t("list.table.mt")}
              </TableHead>
              <TableHead className={headCellClass}>
                {t("list.table.client")}
              </TableHead>
              <TableHead className={headCellClass}>
                {t("list.table.certifications")}
              </TableHead>
              <TableHead className={headCellClass}>
                {t("list.table.analyses")}
              </TableHead>
              <TableHead className={headCellClass}>
                {t("list.table.ot")}
              </TableHead>
              <TableHead className={`${headCellClass} min-w-[120px]`}>
                {t("list.table.status")}
              </TableHead>
              <TableHead aria-hidden className={`${headCellClass} w-8`} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {lots.map((lot) => {
              const tone = lotStatusTone(lot.status)
              const netMT = lotNetMT(lot)
              const usinageNumber = lotUsinageNumber(lot)
              const { client, certifications } = lotContext(lot)
              const otLink = boundOTLink(lot)
              return (
                <TableRow
                  key={lot.id}
                  className="hover:bg-muted/60 cursor-pointer"
                  onClick={() => navigate(paths.lotDetail(lot.id))}
                >
                  <TableCell className={bodyCellClass}>
                    <div className="flex flex-col gap-0.5">
                      <span className="font-mono text-sm font-medium">
                        {lot.lotNumber}
                      </span>
                      <span className="text-muted-foreground font-mono text-[11px]">
                        {usinageNumber ?? "—"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell
                    className={`${bodyCellClass} text-right tabular-nums`}
                  >
                    <span className="font-medium">{lot.bags}</span>
                    <span className="text-muted-foreground text-[11px]">
                      {" / "}
                      {LOT_TARGET_BAGS}
                    </span>
                  </TableCell>
                  <TableCell
                    className={`${bodyCellClass} text-right tabular-nums`}
                  >
                    {netMT.toFixed(2)}
                  </TableCell>
                  <TableCell className={bodyCellClass}>
                    {client ?? (
                      <span className="text-muted-foreground/70">—</span>
                    )}
                  </TableCell>
                  <TableCell className={bodyCellClass}>
                    <CertificationCell certificates={certifications} />
                  </TableCell>
                  <TableCell className={bodyCellClass}>
                    <AnalysesCell qcLogs={lot.qcLogs ?? []} />
                  </TableCell>
                  <TableCell className={bodyCellClass}>
                    {otLink?.ot ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(paths.transitOrderDetail(otLink.ot!.id))
                        }}
                        className="text-primary font-mono text-xs hover:underline"
                      >
                        {otLink.ot.otNumber}
                      </button>
                    ) : (
                      <span className="text-muted-foreground/70 text-xs">
                        {t("list.table.otUnassigned")}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className={bodyCellClass}>
                    <StatusBadge tone={tone}>
                      {t(`status.${lot.status}`)}
                    </StatusBadge>
                  </TableCell>
                  <TableCell className={bodyCellClass}>
                    <ChevronRight className="text-muted-foreground size-4" />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </Card>
  )
}

function AnalysesCell({
  qcLogs,
}: {
  qcLogs: NonNullable<LotListItemWithQC["qcLogs"]>
}) {
  const { t } = useTranslation("lots")
  const hasLab = qcLogs.some((l) => l.kind === "LAB")
  const hasClient = qcLogs.some((l) => l.kind === "CLIENT")
  return (
    <div className="flex flex-wrap items-center gap-1">
      <AnalysisChip done={hasLab} label={t("list.table.analysisLab")} />
      <AnalysisChip done={hasClient} label={t("list.table.analysisClient")} />
    </div>
  )
}

function AnalysisChip({ done, label }: { done: boolean; label: string }) {
  return (
    <Badge
      variant="secondary"
      className={cn(
        "gap-1 text-[11px] font-normal",
        done
          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300"
          : "text-muted-foreground/70",
      )}
    >
      <span
        className={cn(
          "size-1.5 rounded-full",
          done ? "bg-emerald-500" : "bg-muted-foreground/40",
        )}
      />
      {label}
    </Badge>
  )
}

function CertificationCell({ certificates }: { certificates: string[] }) {
  const { t } = useTranslation("intake")
  if (certificates.length === 0) {
    return <span className="text-muted-foreground/70">—</span>
  }
  const visible = certificates.slice(0, 2)
  const overflow = certificates.length - visible.length
  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map((cert) => {
        const key = certificateTranslationKey(cert)
        const label = key ? t(key) : cert
        return (
          <Badge
            key={cert}
            variant="secondary"
            className="text-[11px] font-normal"
          >
            {label}
          </Badge>
        )
      })}
      {overflow > 0 ? (
        <span className="text-muted-foreground text-[11px] tabular-nums">
          +{overflow}
        </span>
      ) : null}
    </div>
  )
}
